import type { EditableGridCell, Item } from '@glideapps/glide-data-grid'
import {
  createContext,
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'

import type { AvailableColumn } from '../types'

// List utility functions
const updateAtIndex = <TData>(data: TData, list: readonly TData[], index: number): TData[] => [
  ...list.slice(0, index),
  data,
  ...list.slice(index + 1),
]

export interface DatagridChange {
  data: unknown
  row: number
  column: string
}

export interface DatagridChangeOpts {
  added: number[]
  removed: number[]
  updates: DatagridChange[]
  currentUpdate?: DatagridChange
}
export type OnDatagridChange = (
  opts: DatagridChangeOpts,
  setMarkCellsDirty: (areCellsDirty: boolean) => void,
) => void

export interface UseDatagridChangeState {
  added: number[]
  setAdded: Dispatch<SetStateAction<number[]>>
  removed: number[]
  setRemoved: Dispatch<SetStateAction<number[]>>
  changes: RefObject<DatagridChange[]>
  clear: () => void
}
export function useDatagridChangeState(): UseDatagridChangeState {
  const [added, setAdded] = useState<number[]>([])
  const [removed, setRemoved] = useState<number[]>([])
  const changes = useRef<DatagridChange[]>([])
  const clear = () => {
    changes.current = []
    setAdded([])
    setRemoved([])
  }

  return {
    added,
    changes,
    clear,
    removed,
    setAdded,
    setRemoved,
  }
}

export const DatagridChangeStateContext = createContext<UseDatagridChangeState | undefined>(
  undefined,
)

const useDatagridChangeStateContext = () => {
  const context = useContext(DatagridChangeStateContext)
  if (!context) {
    throw new Error(
      'useDatagridChangeStateContext must be used within DatagridChangeStateContext.Provider',
    )
  }

  return context
}

function useDatagridChange(
  availableColumns: readonly AvailableColumn[],
  rows: number,
  onChange?: OnDatagridChange,
  setMarkCellsDirty?: (areCellsDirty: boolean) => void,
) {
  const { added, setAdded, removed, setRemoved, changes } = useDatagridChangeStateContext()
  const getChangeIndex = useCallback(
    (column: string, row: number): number =>
      changes.current.findIndex((change) => change.column === column && change.row === row),
    [changes],
  )
  const notify = useCallback(
    ({
      added,
      currentUpdate,
      removed,
      updates,
    }: {
      updates: DatagridChange[]
      added: number[]
      removed: number[]
      currentUpdate: DatagridChange | undefined
    }) => {
      if (onChange && setMarkCellsDirty) {
        onChange(
          {
            added,
            currentUpdate,
            removed,
            updates,
          },
          setMarkCellsDirty,
        )
      }
    },
    [onChange, setMarkCellsDirty],
  )
  const onCellEdited = useCallback(
    ([column, row]: Item, newValue: EditableGridCell): void => {
      const columnId = availableColumns[column].id
      const existingIndex = getChangeIndex(columnId, row)
      const update = {
        column: columnId,
        data: newValue.data,
        row,
      }

      // eslint-disable-next-line react-hooks/immutability
      changes.current =
        existingIndex === -1
          ? [...changes.current, update]
          : updateAtIndex(update, changes.current, existingIndex)
      notify({
        added,
        currentUpdate: update,
        removed,
        updates: changes.current,
      })
    },
    [availableColumns, notify, added, removed, changes, getChangeIndex],
  )
  const onRowsRemoved = useCallback(
    (rows: number[]) => {
      const getRowOffset = (row: number) => rows.filter((r) => r < row).length
      const newAdded = added
        .filter((row) => !rows.includes(row))
        .map((row) => row - getRowOffset(row))
      const newRemoved = [
        ...removed,
        ...rows
          .filter((row) => !added.includes(row))
          .map((row) => row + removed.filter((r) => r <= row).length),
      ]

      setRemoved(newRemoved)
      // eslint-disable-next-line react-hooks/immutability
      changes.current = changes.current
        .filter((change) => !rows.includes(change.row))
        .map((change) => ({
          ...change,
          row: change.row - getRowOffset(change.row),
        }))
      setAdded(newAdded)
      notify({
        added: newAdded,
        currentUpdate: undefined,
        removed: newRemoved,
        updates: changes.current,
      })
    },
    [added, removed, notify, changes, setAdded, setRemoved],
  )
  const onRowAdded = useCallback(() => {
    const newAdded = [...added, rows - removed.length + added.length]

    setAdded(newAdded)
    notify({
      added: newAdded,
      currentUpdate: undefined,
      removed,
      updates: changes.current,
    })
  }, [added, notify, removed, rows, changes, setAdded])

  return {
    added,
    changes,
    getChangeIndex,
    onCellEdited,
    onRowAdded,
    onRowsRemoved,
    removed,
  }
}

export default useDatagridChange
